import React, { FC, useState, useEffect, useMemo } from 'react';
import { DebounceSelect, DebounceSelectOptionProps } from '../../../../../components/UI/AntCustom/DebounceSelect';
import styled from 'styled-components';
import { defineMessages, useIntl } from 'react-intl';
import { fetchAirports, getAirportsFromStoreByIds, fetchInitialAirportForSelect } from '../../../../../utils/helpers/selectDataFetchers/fetchAirports';
import { CloseOutlined } from '@ant-design/icons';
import { CustomButton } from '../../../../../components/UI/AntCustom/CustomButton';

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0 !important;

    > * {
      width: 100%;
    }
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledDebounceSelect = styled(DebounceSelect)`
  .ant-select-selector {
    padding: 5px !important;
    min-height: 34px !important;
    height: auto !important;
    max-height: 120px !important;
    border-radius: 5px !important;
    overflow: hidden !important;
  }

  .ant-select-selection-overflow {
    max-height: 110px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    align-items: flex-start !important;
    flex-wrap: wrap !important;
    gap: 4px !important;
  }

  .ant-select-selection-overflow-item {
    max-height: 22px !important;
    max-width: 100% !important;
    flex-shrink: 1 !important;
    min-width: 0 !important;
  }

  .ant-select-selection-item {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: 100% !important;
    margin: 0 !important;
  }
`;

const EventCalendarSimpleFiltersMessages = defineMessages({
  allAirports: {
    id: 'eventCalendarSimpleFilters.allAirports',
    defaultMessage: 'Все аэропорты',
  },
});

interface IEventCalendarSimpleFilters {
  filters: {
    airportIds?: number[];
  };
  onFiltersChange: (filters: {
    airportIds?: number[];
  }) => void;
  // Опциональные пропсы для фильтра по самолетам
  selectedPlaneIds?: number[];
  onPlaneIdsChange?: (planeIds: number[]) => void;
}

export const EventCalendarSimpleFilters: FC<IEventCalendarSimpleFilters> = ({
  filters,
  onFiltersChange,
  selectedPlaneIds,
  onPlaneIdsChange,
}) => {
  const intl = useIntl();
  const [selectedAirports, setSelectedAirports] = useState<DebounceSelectOptionProps[]>([]);

  // Load selected airports from store or API when filters change
  useEffect(() => {
    if (filters.airportIds && filters.airportIds.length > 0) {
      const airportsFromStore = getAirportsFromStoreByIds(filters.airportIds);
      
      // Check if we have all airports in selectedAirports state (from user selection)
      const selectedIds = selectedAirports.map(a => a.value as number);
      const allSelectedMatch = filters.airportIds.every(id => selectedIds.includes(id)) && 
                               selectedAirports.length === filters.airportIds.length;
      
      // If we already have all airports in selectedAirports, don't overwrite
      if (allSelectedMatch) {
        return;
      }
      
      // If we found all in store, use them
      if (airportsFromStore.length === filters.airportIds.length) {
        setSelectedAirports(airportsFromStore);
      } else {
        // Fetch missing airports from API
        const foundIds = new Set(airportsFromStore.map(a => a.value as number));
        const missingIds = filters.airportIds.filter(id => !foundIds.has(id));
        
        if (missingIds.length > 0) {
          Promise.all(
            missingIds.map(id => fetchInitialAirportForSelect(id))
          ).then(results => {
            const fetchedAirports = results
              .filter((result): result is DebounceSelectOptionProps[] => result !== undefined && result.length > 0)
              .flat();
            
            if (fetchedAirports.length > 0) {
              setSelectedAirports([...airportsFromStore, ...fetchedAirports]);
            } else if (airportsFromStore.length > 0) {
              // If we couldn't fetch but have some from store, use what we have
              setSelectedAirports(airportsFromStore);
            }
          }).catch(error => {
            console.error('Error fetching airports:', error);
            // Fallback to store results if available
            if (airportsFromStore.length > 0) {
              setSelectedAirports(airportsFromStore);
            }
          });
        } else if (airportsFromStore.length > 0) {
          setSelectedAirports(airportsFromStore);
        }
      }
    } else {
      setSelectedAirports([]);
    }
  }, [filters.airportIds]); // Removed selectedAirports from deps to avoid infinite loop

  // Build value from selectedAirports - show what we have, even if still fetching
  const value = useMemo(() => {
    if (!filters.airportIds || filters.airportIds.length === 0) {
      return [];
    }
    
    // Return selectedAirports - they will be updated by useEffect when fetched
    // This ensures we show the airports that were selected, even if some are still loading
    return selectedAirports.filter(airport => 
      filters.airportIds!.includes(airport.value as number)
    );
  }, [filters.airportIds, selectedAirports]);

  const hasActiveAirportFilter = filters.airportIds && filters.airportIds.length > 0;
  const hasActivePlaneFilter = selectedPlaneIds && selectedPlaneIds.length > 0;
  const hasActiveFilter = hasActiveAirportFilter || hasActivePlaneFilter;

  const handleClear = () => {
    setSelectedAirports([]);
    onFiltersChange({ ...filters, airportIds: undefined });
    // Сбрасываем фильтр по самолетам, если он передан
    if (onPlaneIdsChange) {
      onPlaneIdsChange([]);
    }
  };

  return (
    <FiltersRow>
      <FilterWrapper>
        <StyledDebounceSelect
          mode={"multiple"}
          fetchOptions={fetchAirports}
          placeholder={intl.formatMessage(EventCalendarSimpleFiltersMessages.allAirports)}
          showSearch={true}
          allowClear={false}
          style={{width: 200}}
          value={value}
          onChange={(values) => {
            if (!values || (Array.isArray(values) && values.length === 0)) {
              setSelectedAirports([]);
              onFiltersChange({ ...filters, airportIds: undefined });
            } else {
              const selected = Array.isArray(values) ? values : [values];
              // Store the selected airport objects
              setSelectedAirports(selected.filter((v): v is DebounceSelectOptionProps => 
                typeof v === 'object' && v !== null && 'value' in v
              ));
              
              const ids = selected
                .map(v => typeof v === 'object' && v !== null && 'value' in v ? v.value : v)
                .filter((id): id is number => typeof id === 'number');
              
              onFiltersChange({
                ...filters,
                airportIds: ids.length > 0 ? ids : undefined,
              });
            }
          }}
        />
        {hasActiveFilter && (
          <CustomButton
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClear}
            style={{ 
              padding: '4px 8px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Clear filter"
          />
        )}
      </FilterWrapper>
    </FiltersRow>
  );
};






